import {
  ZoneBoxTarget,
  ZoneParams,
  ZoneTabKey,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import {
  ZonePrefillDerivationContext,
  ZoneTabAgentPermissions,
} from 'features/libra/stepComponents/zoneAnalysis/zoneTabAutofill'
import { FC, MouseEvent as ReactMouseEvent, useCallback } from 'react'

import { ZoneRow } from './ZoneRow'
import { SourceDocumentLabel, ZoneRowSurface } from './zonesEditorSurfaceStyles'

type Props = {
  zone: ZoneParams
  sourceDocumentName?: string
  selected: boolean
  selectedBoxIndex?: number
  revision: number
  readonly: boolean
  showCoverageBreakdown: boolean
  enableNiiCoverage: boolean
  visibleTabs?: ZoneTabKey[]
  zoneTabAgentPermissions: ZoneTabAgentPermissions
  prefillDerivationContext?: ZonePrefillDerivationContext
  isZoneDirty?: boolean
  isSaving: boolean
  savingZoneId?: string
  saveError?: string
  onRegisterRow: (zoneId: string, element: HTMLDivElement | null) => void
  onSelectZone: (zone: ZoneParams) => void
  onSelectBox: (zone: ZoneParams, boxIndex: number, revision: number) => void
  onAddBox: (zone: ZoneParams, revision: number) => void
  onRedrawBox: (zone: ZoneParams, revision: number) => void
  onRemoveRectangle: (target: ZoneBoxTarget, revision: number) => void
  onUpdateZone: (zoneId: string, updates: Partial<ZoneParams>) => void
  onDeleteZone: (zoneId: string) => void
  onConfirmZone: (zoneId: string) => void
  onSaveZone?: (zoneId: string) => Promise<void>
}

export const ZonesEditorRow: FC<Props> = ({
  zone,
  sourceDocumentName,
  selected,
  selectedBoxIndex,
  revision,
  readonly,
  showCoverageBreakdown,
  enableNiiCoverage,
  visibleTabs,
  zoneTabAgentPermissions,
  prefillDerivationContext,
  isZoneDirty,
  isSaving,
  savingZoneId,
  saveError,
  onRegisterRow,
  onSelectZone,
  onSelectBox,
  onAddBox,
  onRedrawBox,
  onRemoveRectangle,
  onUpdateZone,
  onDeleteZone,
  onConfirmZone,
  onSaveZone,
}) => {
  const setRowRef = useCallback(
    (element: HTMLDivElement | null) => onRegisterRow(zone.id, element),
    [onRegisterRow, zone.id]
  )
  const handleSelectZone = () => onSelectZone(zone)
  const handleRowClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    const target = event.target
    if (
      target instanceof Element &&
      target.closest(
        'button, input, textarea, select, a, [contenteditable="true"]'
      )
    )
      return
    handleSelectZone()
  }
  const handleSelectBox = (boxIndex: number) =>
    onSelectBox(zone, boxIndex, revision)
  const handleAddBox = () => onAddBox(zone, revision)
  const handleRedrawBox = () => onRedrawBox(zone, revision)
  const handleRemoveRectangle = () =>
    onRemoveRectangle(
      { zoneId: zone.id, boxIndex: selectedBoxIndex ?? 0 },
      revision
    )
  const handleUpdate = (updates: Partial<ZoneParams>) =>
    onUpdateZone(zone.id, updates)
  const handleDelete = () => onDeleteZone(zone.id)
  const handleConfirm = () => onConfirmZone(zone.id)
  const handleSave = async () => onSaveZone?.(zone.id)

  return (
    <ZoneRowSurface ref={setRowRef} onClick={handleRowClick}>
      <ZoneRow
        zone={zone}
        totalAssetArea={undefined}
        selected={selected}
        selectedBoxIndex={selectedBoxIndex}
        onSelect={handleSelectZone}
        onSelectBox={handleSelectBox}
        onAddBox={readonly ? undefined : handleAddBox}
        readonly={readonly}
        onDelete={readonly ? undefined : handleDelete}
        onUpdate={readonly ? undefined : handleUpdate}
        onRedraw={readonly ? undefined : handleRedrawBox}
        onRemoveRectangle={readonly ? undefined : handleRemoveRectangle}
        onConfirm={readonly ? undefined : handleConfirm}
        onReject={readonly ? undefined : handleDelete}
        showCoverageBreakdown={showCoverageBreakdown}
        showRectangleStatus
        enableNiiCoverage={enableNiiCoverage}
        visibleTabs={visibleTabs}
        zoneTabAgentPermissions={zoneTabAgentPermissions}
        prefillDerivationContext={prefillDerivationContext}
        onSaveZone={readonly || !onSaveZone ? undefined : handleSave}
        isZoneDirty={isZoneDirty}
        isSaving={isSaving}
        savingZoneId={savingZoneId}
        saveError={saveError}
      />
      <SourceDocumentLabel>
        {zone.zone_reference_image_id
          ? sourceDocumentName || 'Unknown document'
          : zone.componentId
            ? `Asset Model Zone (Component: ${zone.componentId})`
            : 'No source specified'}
      </SourceDocumentLabel>
    </ZoneRowSurface>
  )
}
